import type { LoaderFunctionArgs } from '@remix-run/node'
import { json, useLoaderData } from '@remix-run/react'

import { Layout, type LayoutProps } from '~/components/Typography/Layout'
import { fetchQuery } from '~/graphql/fetchQuery'
import { graphql } from '~/lib/graphql'
import { validateRestaurantId } from '~/lib/graphql/scalars'
import { css } from '~/styled-system/css'

const RestaurantPolicyInfo = graphql(`
  query RestaurantPolicyInfo($restaurantId: RestaurantId!) {
    restaurant(restaurantId: $restaurantId) {
      name
      fullAddress
      phoneNumber
    }
  }
`)

export const loader = import.meta.env.VITE_MODE === 'ppg' ? ppgLoader : ikyuLoader

async function ppgLoader({ params, request }: LoaderFunctionArgs) {
  const restaurantId = validateRestaurantId(params.restaurantId)
  if (!restaurantId) {
    throw new Response('Not Found', { status: 404 })
  }
  const data = await fetchQuery(RestaurantPolicyInfo, { restaurantId }, request)
  if (!data.restaurant) {
    throw new Response('Not Found', { status: 404 })
  }
  return json({ restaurant: data.restaurant })
}

function ikyuLoader() {
  throw new Response('Not Found', { status: 404 })
}

export default function () {
  const { restaurant } = useLoaderData<typeof loader>()
  const title = `${restaurant.name}のプライバシーポリシー・特定商取引法に基づく表記`
  return <Layout title={title} sections={sections(restaurant)} />
}

function sections({
  name,
  fullAddress,
  phoneNumber,
}: { name: string; fullAddress: string; phoneNumber: string }): LayoutProps['sections'] {
  return [
    {
      title: 'プライバシーポリシー（利用目的）',
      description: (
        <p>
          ご予約に関する情報（個人情報を含みます）を、次の利用目的の達成に必要な範囲内で利用します。
          <br />
          ・お客様のご予約の確認、お客様からのお問い合わせ対応、その他必要に応じてお客様との連絡をとること
          <br />
          <br />
          ※ご予約に関する情報（個人情報を含みます）は、株式会社一休、LINEヤフー株式会社がそれぞれ取得しております。
          <br />
          ※株式会社一休より予約店舗にご予約に関する情報を提供し、ご予約店舗が管理・利用する当該情報に関する開示・訂正・削除・利用停止等につきましては、直接店舗へお問い合わせください。
        </p>
      ),
    },
    {
      title: '特定商取引法に基づく表記',
      description: null,
      subsections: [
        {
          title: '店舗関連情報',
          content: (
            <p>
              {name}
              <br />
              {fullAddress}
              <br />
              {phoneNumber}
              <br />
              <br />
              ※記載のない情報に関しましてはPayPayグルメのヘルプ「店舗情報について」よりお問い合わせいただければメールにてご連絡いたします。
            </p>
          ),
        },
        {
          title: '料金',
          content: (
            <p>
              席のみのご予約：現地でのご注文金額によります。
              <br />
              コースのご予約：各プランの詳細ページに記載のとおりです。現地での追加注文分等は含まれません。
            </p>
          ),
        },
        {
          title: '予約金額以外に発生する料金等',
          content: <p>別途発生する料金等は各プランの詳細ページをご覧ください。</p>,
        },
        {
          title: 'サービス提供時期',
          content: <p>店舗へのご来店時になります。</p>,
        },
        {
          title: 'キャンセルについて',
          content: (
            <p>
              ご予約店舗の各プランの詳細ページをご覧ください。
              <br />
              キャンセル料が発生した場合、ご予約店舗に代わって、株式会社一休からキャンセル料を請求させていただく場合がございます。
              <br />
              対象のご予約は各プランの詳細ページをご覧ください。
            </p>
          ),
        },
        {
          title: '予約期限があるプラン等について',
          content: (
            <p>
              期間限定のプラン等に関しては、ご予約店舗の各プランの詳細ページをご覧ください。
              <br />
              ※ご予約状況によって、予告なく終了する場合がございます。
            </p>
          ),
        },
        {
          title: 'お支払い方法・お支払い時期',
          content: (
            <p>
              <span className={css({ fontWeight: 'bold' })}>支払方法</span>
              <br />
              ご予約店舗の各プランの詳細をご覧ください。
              <br />
              記載のない情報に関しましてはヘルプ・お問い合わせのページよりお問い合わせいただければメールにてご連絡いたします。
              <br />
              <br />
              <span className={css({ fontWeight: 'bold' })}>お支払時期</span>
              <br />
              店舗へのご来店時（決済時）となります。
              <br />
              予約時の決済にPayPay（残高/ポイント）支払いを利用した場合、決済金額は、ご予約時に同額のPayPay（残高/ポイント）が利用され、決済が完了します。
              <br />
              （なお、ご予約をキャンセルされた場合、返金処理が行われ、返金された金額はすみやかにユーザーの残高に戻ります。）
            </p>
          ),
        },
        {
          title: '特別な予約条件等',
          content: <p>ご予約店舗の各プランの詳細ページをご覧ください。</p>,
        },
        {
          title: '飲食サービスに関する責任について',
          content: <p>ご予約店舗の各プランの詳細ページご覧ください。</p>,
        },
      ],
    },
  ]
}
